import { midiToFreq } from '../../utils/math';

/**
 * SpecialFX
 * 
 * Handles "one-off" generative effects that add virtuosity and depth.
 * - Harmonic Bloom: Fades in chords during empty moments.
 * - Glissando Trails: Creates harp-like runs during high intensity.
 */

export interface FXContext {
    scaleNotes: number[]; // Full scale MIDI notes
    lastTouchFreq: number;
    lastTouchVelocity: number;
    density: 'sparse' | 'normal' | 'dense';
    tuning: number;
}

export class SpecialFX {
    // BLOOM STATE
    private lastBloomTime: number = 0;
    private readonly BLOOM_COOLDOWN = 4000; // 4s

    constructor() {
        this.reset();
    }

    reset() {
        this.lastBloomTime = 0;
    }

    /**
     * Check for and generate "Harmonic Bloom" events.
     * Triggered when playing is sparse and space allows.
     */
    getBloomEvents(context: FXContext): Array<{ freq: number, delay: number, velocity: number, duration: number, timbre: any }> {
        const now = Date.now();

        // 1. Must be SPARSE density (room to breathe)
        if (context.density !== 'sparse') return [];

        // 2. Cooldown check
        if (now - this.lastBloomTime < this.BLOOM_COOLDOWN) return [];

        // 3. Probability (don't happen every time)
        if (Math.random() > 0.25) return []; // 25% chance

        // GENERATE BLOOM
        // Stack 2 notes above the last touch
        // Ideally 3rds and 5ths, using scale
        const baseMidi = Math.round(69 + 12 * Math.log2(context.lastTouchFreq / 440));

        // Find base in scale
        // Simple closest note logic
        let closestIndex = -1;
        let minDiff = 999;

        context.scaleNotes.forEach((note, index) => {
            const diff = Math.abs(note - baseMidi);
            if (diff < minDiff) {
                minDiff = diff;
                closestIndex = index;
            }
        });

        if (closestIndex === -1) return [];

        const events = [];

        // Add 3rd (approx +2 steps in heptatonic scale)
        if (closestIndex + 2 < context.scaleNotes.length) {
            const noteMidi = context.scaleNotes[closestIndex + 2];
            events.push({
                freq: midiToFreq(noteMidi, context.tuning),
                delay: 0.2, // Fade in later
                velocity: context.lastTouchVelocity * 0.6, // Softer
                duration: 4.0, // Long sustain
                timbre: { release: 4.0, filter: 0.3, reverb: 0.8 } // Ambient
            });
        }

        // Add 5th (approx +4 steps)
        if (closestIndex + 4 < context.scaleNotes.length) {
            const noteMidi = context.scaleNotes[closestIndex + 4];
            events.push({
                freq: midiToFreq(noteMidi, context.tuning),
                delay: 0.4, // Staggered
                velocity: context.lastTouchVelocity * 0.5,
                duration: 5.0,
                timbre: { release: 5.0, filter: 0.25, reverb: 0.9 }
            });
        }

        if (events.length > 0) {
            this.lastBloomTime = now;
            if (process.env.NODE_ENV === 'development') console.log('[SpecialFX] Harmonic Bloom Triggered 🌸');
        }

        return events;
    }
}
